/**
 * Qlivar — Marketing primitives.
 *
 * Shell-derived primitives consumed by public surfaces (landing, auth chrome,
 * pricing, blog, AI feature pages) so the marketing site inherits a single
 * visual culture instead of reinventing the same eyebrow / heading / panel
 * recipe per page.
 *
 * Conventions (do not violate without a written reason):
 *   • Eyebrow grammar    — 10.5px / uppercase / tracking-[0.22em] / mono
 *   • Section ladder      — eyebrow → tight title → restrained lede
 *   • Panel surface       — bg-card/40 + border-border/60 + accent blob + 1px hairline
 *   • Brand mark          — token-driven (no hardcoded hex)
 *   • KBD chips           — operator vocabulary, sparingly
 *
 * Anti-generic guards: avoid mesh-gradient backgrounds, animate-spin motion-reduce:animate-none, gradient
 * text, "Get started for free" CTAs, default shadcn Card without override.
 *
 * Inspired by the canonical asenbura/src/shared/landing/_primitives.tsx and
 * adapted to qlivar's warm-cream palette (token-driven, not hex-locked).
 */

import { forwardRef } from "react";
import { motion, type HTMLMotionProps } from "framer-motion";
import { cn } from "@/lib/utils";

/* -------------------------------------------------------------------------- */
/*  Eyebrow                                                                   */
/* -------------------------------------------------------------------------- */

export interface EyebrowProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Optional accent HSL string (e.g. `15 90% 58%`). Defaults to --primary. */
  accent?: string;
  withDot?: boolean;
}

export const Eyebrow = forwardRef<HTMLSpanElement, EyebrowProps>(
  ({ className, accent, withDot = false, children, ...rest }, ref) => (
    <span
      ref={ref}
      className={cn(
        "inline-flex items-center gap-2 font-mono text-[10.5px] font-semibold uppercase tracking-[0.22em]",
        "text-muted-foreground/75",
        className,
      )}
      {...rest}
    >
      {withDot ? (
        <span
          aria-hidden
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{
            background: accent ? `hsl(${accent})` : "hsl(var(--primary))",
            boxShadow: accent
              ? `0 0 8px hsl(${accent} / 0.55)`
              : "0 0 8px hsl(var(--primary) / 0.55)",
          }}
        />
      ) : null}
      {children}
    </span>
  ),
);
Eyebrow.displayName = "Eyebrow";

/* -------------------------------------------------------------------------- */
/*  SectionHeader                                                              */
/* -------------------------------------------------------------------------- */

export interface SectionHeaderProps {
  eyebrow: string;
  title: React.ReactNode;
  lede?: React.ReactNode;
  align?: "left" | "center";
  accent?: string;
  className?: string;
}

export const SectionHeader = ({
  eyebrow,
  title,
  lede,
  align = "left",
  accent,
  className,
}: SectionHeaderProps) => (
  <div
    className={cn(
      "flex flex-col gap-4",
      align === "center" ? "items-center text-center" : "items-start",
      className,
    )}
  >
    <Eyebrow accent={accent} withDot>
      {eyebrow}
    </Eyebrow>
    <h2 className="max-w-3xl font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
      {title}
    </h2>
    {lede ? (
      <p
        className={cn(
          "max-w-2xl text-[13.5px] leading-relaxed text-muted-foreground",
          align === "center" ? "mx-auto" : "",
        )}
      >
        {lede}
      </p>
    ) : null}
  </div>
);

/* -------------------------------------------------------------------------- */
/*  MarketingSection                                                           */
/* -------------------------------------------------------------------------- */

export interface MarketingSectionProps
  extends React.HTMLAttributes<HTMLElement> {
  id?: string;
  eyebrow?: string;
  surface?: "transparent" | "tinted";
  compact?: boolean;
}

export const MarketingSection = forwardRef<
  HTMLElement,
  MarketingSectionProps
>(
  (
    { id, className, surface = "transparent", compact = false, children, ...rest },
    ref,
  ) => (
    <section
      ref={ref}
      id={id}
      className={cn(
        "relative",
        compact ? "py-20" : "py-24 md:py-28",
        surface === "tinted" ? "bg-secondary/15" : "",
        className,
      )}
      {...rest}
    >
      <div className="container relative z-10 px-6">{children}</div>
    </section>
  ),
);
MarketingSection.displayName = "MarketingSection";

/* -------------------------------------------------------------------------- */
/*  PanelSurface — canonical bg-card/40 + border + hairline + accent blob      */
/* -------------------------------------------------------------------------- */

export interface PanelSurfaceProps extends HTMLMotionProps<"div"> {
  accent?: string;
  blob?: "top-right" | "top-left" | "bottom-right" | "bottom-left" | "none";
  hairline?: boolean;
}

const BLOB_POSITION = {
  "top-right": "-right-32 -top-32",
  "top-left": "-left-32 -top-32",
  "bottom-right": "-right-32 -bottom-32",
  "bottom-left": "-left-32 -bottom-32",
  none: "hidden",
} as const;

export const PanelSurface = forwardRef<HTMLDivElement, PanelSurfaceProps>(
  (
    {
      className,
      accent,
      blob = "top-right",
      hairline = true,
      style,
      children,
      ...rest
    },
    ref,
  ) => {
    const accentVar = accent ?? "var(--primary)";
    const accentHairline = accent
      ? `hsl(${accent} / 0.6)`
      : "hsl(var(--primary) / 0.6)";
    return (
      <motion.div
        ref={ref}
        className={cn(
          "relative overflow-hidden rounded-2xl border border-border/60 bg-card/40",
          className,
        )}
        style={
          {
            ["--module-accent" as string]: accentVar,
            ...style,
          } as React.CSSProperties
        }
        {...rest}
      >
        <span
          aria-hidden
          className={cn(
            "pointer-events-none absolute h-[440px] w-[440px] rounded-full opacity-60 blur-3xl",
            BLOB_POSITION[blob],
          )}
          style={{
            background: accent
              ? `hsl(${accent} / 0.12)`
              : "hsl(var(--primary) / 0.10)",
          }}
        />
        {hairline ? (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{
              background: `linear-gradient(90deg, transparent, ${accentHairline}, transparent)`,
            }}
          />
        ) : null}
        {children}
      </motion.div>
    );
  },
);
PanelSurface.displayName = "PanelSurface";

/* -------------------------------------------------------------------------- */
/*  Kbd chip                                                                   */
/* -------------------------------------------------------------------------- */

export const Kbd = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <kbd
    className={cn(
      "inline-flex h-5 min-w-5 select-none items-center justify-center rounded-md border border-border/60 bg-secondary/40 px-1.5 font-mono text-[10px] font-medium text-muted-foreground/90",
      className,
    )}
  >
    {children}
  </kbd>
);

/* -------------------------------------------------------------------------- */
/*  AmbientBackdrop — token-driven, replaces hex-locked landing backgrounds    */
/* -------------------------------------------------------------------------- */

export const AmbientBackdrop = ({
  className,
  variant = "warm",
}: {
  className?: string;
  variant?: "warm" | "cool" | "neutral";
}) => {
  const blobA =
    variant === "cool"
      ? "hsl(var(--accent) / 0.08)"
      : variant === "neutral"
        ? "hsl(var(--muted-foreground) / 0.05)"
        : "hsl(var(--primary) / 0.08)";
  const blobB =
    variant === "cool"
      ? "hsl(var(--primary) / 0.05)"
      : variant === "neutral"
        ? "hsl(var(--card-foreground) / 0.04)"
        : "hsl(var(--accent) / 0.05)";
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none fixed inset-0 -z-10 overflow-hidden",
        className,
      )}
    >
      <span
        className="absolute -top-40 left-[5%] h-[600px] w-[600px] rounded-full blur-[160px]"
        style={{ background: blobA }}
      />
      <span
        className="absolute -bottom-40 right-[5%] h-[440px] w-[440px] rounded-full blur-[140px]"
        style={{ background: blobB }}
      />
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  EmptyStudio — canonical signature empty state (no icon-bubble)             */
/* -------------------------------------------------------------------------- */
/**
 * Replaces the predictable `<div round-bg-muted><Icon/></div> + headline + subtext`
 * empty pattern with an editorial composition: hairline + mono kicker + italic
 * line. Use everywhere across product surfaces (analytics, chat history, lists,
 * dashboards) to enforce a single, signature voice when there is no data yet.
 */

export interface EmptyStudioProps {
  /** Short uppercase mono label (e.g. "Sinal · 0", "Tráfego · em silêncio"). */
  kicker: string;
  /** Editorial italic line — short, declarative, in voice. */
  title: React.ReactNode;
  /** Optional supporting hint, one-line preferred. */
  hint?: React.ReactNode;
  align?: "center" | "left";
  /** Optional accent HSL string (defaults to --primary). */
  accent?: string;
  className?: string;
}

export const EmptyStudio = ({
  kicker,
  title,
  hint,
  align = "center",
  accent,
  className,
}: EmptyStudioProps) => {
  const ruleColor = accent
    ? `hsl(${accent} / 0.5)`
    : "hsl(var(--primary) / 0.45)";
  const dotColor = accent ? `hsl(${accent})` : "hsl(var(--primary))";
  return (
    <div
      className={cn(
        "relative flex flex-col gap-3 py-10 px-5",
        align === "center" ? "items-center text-center" : "items-start text-left",
        className,
      )}
    >
      <span
        aria-hidden
        className="h-px w-14"
        style={{
          background: `linear-gradient(90deg, transparent, ${ruleColor}, transparent)`,
        }}
      />
      <span className="inline-flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground/75">
        <span
          aria-hidden
          className="h-1 w-1 rounded-full"
          style={{ background: dotColor, boxShadow: `0 0 6px ${dotColor}` }}
        />
        {kicker}
      </span>
      <p className="font-editorial italic text-[1.05rem] leading-snug text-foreground/85 max-w-[30ch]">
        {title}
      </p>
      {hint ? (
        <p className="text-[12px] leading-relaxed text-muted-foreground/85 max-w-[36ch]">
          {hint}
        </p>
      ) : null}
    </div>
  );
};


